import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CircularProgress, IconButton, Paper, Typography, Box, Chip } from "@mui/material";
import { ChevronLeft, Activity, Focus, Eye, Smile, Sparkles, ScanFace } from "lucide-react";
import { kioskModel } from "../Axios/AxiosConfig";

/* ─── helpers ─────────────────────────────────────────────── */
const toTitleCase = (str) =>
  str ? String(str).trim().replace(/\b\w/g, (c) => c.toUpperCase()) : "";

const isValidValue = (v) => {
  if (v === null || v === undefined) return false;
  const s = String(v).trim().toLowerCase();
  return !["", "n/a", "na", "--", "null", "undefined", "none"].includes(s);
};

/** Read a CSS variable from :root */
const getCssVar = (name, fallback = "") => {
  const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return val || fallback;
};

/** Convert hex color to rgba string */
const hexToRgba = (hex, alpha) => {
  const h = (hex || "#DA6E0B").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

const ICON_MAP = {
  "Symmetry Assessment": Activity,
  "Facial Index": ScanFace,
  "Nasal Index": Focus,
  "Nose Structure": Focus,
  "Eye Features": Eye,
  "Mouth Features": Smile,
  "Skin Wrinkles": Sparkles,
};

/* ─── component ───────────────────────────────────────────── */
const FacialLandmarkResult = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [facialData, setFacialData] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);

  /* Read actual theme hex from CSS variable on mount */
  const primaryHex = useRef("#DA6E0B");
  useEffect(() => {
    const val = getCssVar("--themePrimaryColor", "#DA6E0B");
    primaryHex.current = val.startsWith("#") ? val : "#DA6E0B";
  }, []);

  /* Shortcuts */
  const tc = `var(--themePrimaryColor, #DA6E0B)`;
  const rgba = (a) => hexToRgba(primaryHex.current, a);

  useEffect(() => {
    (async () => {
      try {
        const res = await kioskModel.get(`api/v1/getClinicalFacialLandmark/${uid}`);
        if (res.status === 200 && res.data?.data) setFacialData(res.data.data);
      } catch (err) {
        console.error("Error fetching facial landmark data:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  /* ── loading ── */
  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, background: "#FDFDFD" }}>
        <CircularProgress sx={{ color: tc }} />
        <Typography sx={{ color: "#918165", fontFamily: "'Poppins', sans-serif" }}>
          Analyzing Facial Records…
        </Typography>
      </Box>
    );
  }

  /* ── empty ── */
  if (!facialData) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, p: 4, textAlign: "center", background: "#FDFDFD" }}>
        <ScanFace size={80} color="#D1C9BC" />
        <Typography variant="h5" sx={{ color: "#3B362A", fontWeight: 700, fontFamily: "'Poppins', sans-serif" }}>
          No Facial Records Found
        </Typography>
        <Typography sx={{ color: "#918165", fontFamily: "'Poppins', sans-serif" }}>
          This patient hasn't completed the Facial Landmark Analysis yet.
        </Typography>
        <button
          onClick={() => navigate(-1)}
          style={{ border: `1.5px solid ${tc}`, color: tc, background: "none", borderRadius: 10, padding: "8px 24px", cursor: "pointer", fontWeight: 600, fontSize: 14, fontFamily: "'Poppins', sans-serif" }}
        >
          Back to Profile
        </button>
      </Box>
    );
  }

  // ── Compute Facial Symmetry ───────────────────────────
  let symmetryLabel = facialData.symmetryDescriptor;
  if (facialData.symmetryScore !== null && facialData.symmetryScore !== undefined) {
    const rawSymmetryScore = Number(facialData.symmetryScore);
    const hasSymmetry = Number.isFinite(rawSymmetryScore);
    if (hasSymmetry) {
      const asymmetryScore = 100 - rawSymmetryScore;
      symmetryLabel = asymmetryScore > 7.5 ? "Asymmetry" : "Symmetry";
    }
  }

  /* ── helper formatting ── */
  const formatNasalIndex = (val) => {
    if (val === undefined || val === null) return null;
    const n = Number(val);
    if (n < 60) return "Narrow";
    if (n >= 60 && n <= 70) return "Balanced";
    return "Broad";
  };

  const formatFacialIndex = (val) => {
    if (val === undefined || val === null) return null;
    const n = Number(val);
    if (n <= 86) return "Narrow";
    if (n > 86 && n <= 96) return "Balanced";
    return "Broad";
  };

  /* ── build metrics — skip N/A ── */
  const metrics = [
    { label: "Symmetry Assessment", value: symmetryLabel },
    { label: "Facial Index", value: formatFacialIndex(facialData.facialIndex) },
    { label: "Nasal Index", value: formatNasalIndex(facialData.nasalIndex) },
    { label: "Nose Structure", value: facialData.noseDescriptor },
    { label: "Eye Features", value: facialData.eyeDescriptor },
    { label: "Mouth Features", value: facialData.mouthDescriptor },
    {
      label: "Skin Wrinkles",
      value: facialData.wrinklesSeverity && isValidValue(facialData.wrinklesSeverity)
        ? `${facialData.wrinklesSeverity}${facialData.wrinklesScore ? ` (Score: ${facialData.wrinklesScore})` : ""}`
        : null,
    },
  ].filter((m) => isValidValue(m.value));

  /* Gradients built from actual theme hex */
  const stripGradient = `linear-gradient(135deg, ${primaryHex.current} 0%, ${rgba(0.65)} 100%)`;
  const cardHovGradient = `linear-gradient(135deg, #fff 65%, ${rgba(0.06)} 100%)`;

  return (
    <Box sx={{ minHeight: "100vh", background: "#F4F6F8", fontFamily: "'Poppins', sans-serif" }}>

      {/* ── Sticky Header ── */}
      <Box sx={{
        position: "sticky", top: 0, zIndex: 10,
        background: "rgba(255,255,255,0.94)",
        backdropFilter: "blur(12px)",
        borderBottom: "1px solid #E2E8F0",
        boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        display: "flex", alignItems: "center", gap: 1.5, px: 4, py: 1.5,
      }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: tc, "&:hover": { background: rgba(0.08) } }}>
          <ChevronLeft />
        </IconButton>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", background: tc, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ScanFace size={18} color="#fff" />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#1E293B", fontSize: 17, fontFamily: "'Poppins', sans-serif", lineHeight: 1.2 }}>
              Facial Landmark Analysis
            </Typography>
            <Typography sx={{ color: "#94A3B8", fontSize: 11, fontFamily: "'Poppins', sans-serif" }}>
              Patient UID: {uid}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Page Body ── */}
      <Box sx={{ maxWidth: 1200, margin: "0 auto", p: { xs: 2, sm: 4 } }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 4, alignItems: "stretch" }}>

          {/* ── LEFT: Image Panel ── */}
          <Box sx={{ flexShrink: 0, width: { xs: "100%", md: 360 } }}>
            <Paper elevation={0} sx={{
              borderRadius: "24px",
              overflow: "hidden",
              border: `1px solid ${rgba(0.18)}`,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              background: "#fff",
              boxShadow: `0 4px 24px ${rgba(0.1)}`,
            }}>
              {/* theme-gradient top strip */}
              <Box sx={{ background: stripGradient, p: 2.5, display: "flex", alignItems: "center", gap: 1 }}>
                <ScanFace size={18} color="rgba(255,255,255,0.9)" />
                <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: 13, fontFamily: "'Poppins', sans-serif", letterSpacing: "0.05em" }}>
                  AI Facial Scan
                </Typography>
              </Box>

              {/* image */}
              <Box sx={{ flex: 1, p: 2.5, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Box
                  component="img"
                  src={facialData.landmarkUrl || facialData.imageUrl}
                  alt="Facial Landmark"
                  sx={{
                    width: "100%",
                    minHeight: 400,
                    maxHeight: 520,
                    objectFit: "cover",
                    borderRadius: "16px",
                    boxShadow: `0 8px 32px ${rgba(0.15)}`,
                    display: "block",
                    border: `1.5px solid ${rgba(0.12)}`,
                  }}
                />
              </Box>

              {/* skin tone row */}
              {facialData.skinColorHex && isValidValue(facialData.skinColorName) && (
                <Box sx={{ px: 2.5, pb: 2.5, pt: 1.5, display: "flex", alignItems: "center", gap: 1.5, borderTop: `1px solid ${rgba(0.1)}` }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: facialData.skinColorHex, border: "2px solid #E2E8F0", flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.1em", fontFamily: "'Poppins', sans-serif" }}>
                      Skin Tone
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1E293B", fontFamily: "'Poppins', sans-serif" }}>
                      {toTitleCase(facialData.skinColorName)}
                    </Typography>
                  </Box>
                  {isValidValue(facialData.skinType) && (
                    <Chip
                      label={toTitleCase(facialData.skinType)}
                      size="small"
                      sx={{ ml: "auto", background: rgba(0.1), color: tc, fontWeight: 700, fontSize: 11, fontFamily: "'Poppins', sans-serif" }}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </Box>

          {/* ── RIGHT: Metrics Panel ── */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>

            {/* Overview card */}
            <Box sx={{ background: "#fff", borderRadius: "20px", border: `1px solid ${rgba(0.14)}`, p: 3, boxShadow: `0 2px 12px ${rgba(0.07)}` }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: tc, fontFamily: "'Poppins', sans-serif", mb: 0.5 }}>
                Analysis Results
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: "#1E293B", fontFamily: "'Poppins', sans-serif", mb: 0.5 }}>
                Facial Metrics Overview
              </Typography>
              {/* <Typography sx={{ color: "#64748B", fontSize: 13, fontFamily: "'Poppins', sans-serif", mb: 2 }}>
                {metrics.length} metric{metrics.length !== 1 ? "s" : ""} analyzed for this patient.
              </Typography> */}


            </Box>

            {/* ── Metric Cards Grid ── */}
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: "16px", alignContent: "start" }}>
              {metrics.map((m, i) => {
                const Icon = ICON_MAP[m.label] || Activity;
                const isHov = hoveredCard === i;
                return (
                  <Paper
                    key={i}
                    elevation={0}
                    onMouseEnter={() => setHoveredCard(i)}
                    onMouseLeave={() => setHoveredCard(null)}
                    sx={{
                      borderRadius: "18px",
                      p: "20px",
                      border: isHov ? `1.5px solid ${primaryHex.current}` : `1px solid ${rgba(0.14)}`,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      position: "relative",
                      overflow: "hidden",
                      boxShadow: isHov ? `0 8px 28px ${rgba(0.18)}` : `0 1px 6px ${rgba(0.07)}`,
                      transform: isHov ? "translateY(-3px)" : "translateY(0)",
                      transition: "all 0.22s ease",
                      cursor: "default",
                      background: isHov ? cardHovGradient : "#fff",
                    }}
                  >
                    {/* corner accent blob */}
                    <Box sx={{ position: "absolute", top: -16, right: -16, width: 64, height: 64, borderRadius: "50%", background: tc, opacity: isHov ? 0.14 : 0.06, transition: "opacity 0.22s" }} />

                    {/* icon box */}
                    <Box sx={{ width: 44, height: 44, borderRadius: "14px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: isHov ? rgba(0.14) : rgba(0.08), transition: "background 0.22s" }}>
                      <Icon size={20} style={{ color: primaryHex.current }} />
                    </Box>

                    {/* label */}
                    <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#94A3B8", fontFamily: "'Poppins', sans-serif" }}>
                      {m.label}
                    </Typography>

                    {/* value */}
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1E293B", fontFamily: "'Poppins', sans-serif", position: "relative", zIndex: 1, lineHeight: 1.3 }}>
                      {toTitleCase(m.value)}
                    </Typography>
                  </Paper>
                );
              })}
            </Box>
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default FacialLandmarkResult;
